import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import React, { useCallback, useEffect, useState } from 'react';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import { useT } from '@/contexts/I18nContext';
import { usePageTitle } from '@/contexts/PageHeaderContext';
import { useAppActions } from '@/app/appContext';
import { getPortfolioSummary, type PortfolioZoneDto } from '@/api/portfolio';
import { rankZones, ZoneHealthCard } from '@/components/portfolio/ZoneHealthCard';

// Superuser portfolio page (FE-39). The card + ranking logic is shared with
// the «Все филиалы» boards — see components/portfolio/ZoneHealthCard.tsx.

const INITIAL_COUNT = 5;
const PAGE_SIZE = 10;
const PAGINATE_THRESHOLD = 15;

const PortfolioPage: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const { setZone, setCamera, setSelectedCamera, setFrame } = useAppActions();

  usePageTitle(t('portfolio.title'), t('portfolio.subtitle'));

  // Selecting a branch from the portfolio must override the previously-picked
  // branch in app state (and clear the per-branch camera state) — otherwise the
  // dashboard would re-sync the URL back to the old zone.
  const openZone = useCallback((zoneId: string) => {
    setZone(zoneId);
    setCamera('');
    setSelectedCamera(null);
    setFrame(null);
    navigate(`/usage/dashboard?zone=${zoneId}`);
  }, [navigate, setZone, setCamera, setSelectedCamera, setFrame]);

  const { data, isLoading, error } = useSWR(
    'portfolio-summary',
    () => Promise.race<PortfolioZoneDto[]>([
      getPortfolioSummary().then(r => r.data),
      // 20s, not 8s: this is now the multi-branch owner's entry point (the
      // report cover), and the cold per-zone summary for 8-10 branches can run
      // several seconds before the 90s server cache warms. Failing their home
      // screen on a slow first load is worse than waiting.
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('portfolio_timeout')), 20_000)
      ),
    ]),
    { refreshInterval: 60_000 },
  );

  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);

  // Reset pagination when data changes (e.g. after a refresh that changes count)
  useEffect(() => {
    if (!data) return;
    setVisibleCount(data.length > PAGINATE_THRESHOLD ? INITIAL_COUNT : data.length);
  }, [data?.length]);

  // Single-zone shortcut: redirect immediately to dashboard
  useEffect(() => {
    if (data && data.length === 1) {
      const id = String(data[0].zone_id);
      setZone(id);
      navigate(`/usage/dashboard?zone=${id}`, { replace: true });
    }
  }, [data, navigate, setZone]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  if (!data) {
    return (
      <ServerStatusNotice error={error} />
    );
  }

  if (data.length === 0) {
    return (
      <div className="p-6 text-sm text-muted-foreground">{t('portfolio.noZones')}</div>
    );
  }

  const ranked = rankZones(data);
  const visible = ranked.slice(0, visibleCount);
  const remaining = ranked.length - visibleCount;

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map(zone => (
          <ZoneHealthCard key={zone.zone_id} zone={zone} onOpen={openZone} />
        ))}
      </div>
      {remaining > 0 && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setVisibleCount(v => Math.min(v + PAGE_SIZE, data.length))}
            className="rounded-md border border-border px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            {t('portfolio.showMore', { n: Math.min(remaining, PAGE_SIZE) })}
          </button>
        </div>
      )}
    </div>
  );
};

export default PortfolioPage;
